import { useMutation, useQuery } from "@tanstack/react-query";
import { dispensingService } from "modules/Pharmacy/Dispensing/api/dispensing.api";
import { GetUserData } from "common/service/CommonService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

export const useDispensingLog = ({
  searchText,
  pageNumber,
  pageSize,
  dispensingid,
  getStudyDropDown,
  studyid,
  periodid,
  enableSelectall = false,
  getIpDropdown = false,
}: {
  searchText?: string | null;
  pageNumber?: number;
  pageSize?: number;

  dispensingid?: number | null;
  getStudyDropDown?: boolean | null;
  studyid?: number | null;
  periodid?: number | null;
  enableSelectall?: boolean;
  getIpDropdown?: boolean;
} = {}) => {
  const { hasPermission } = usePermission();
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);

  /* =========================
     INSERT
  ========================= */
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: dispensingService.insert,
  });

  /* =========================
     UPDATE
  ========================= */
  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: dispensingService.update,
  });

  /* =========================
     DELETE
  ========================= */
  const DeleteMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: dispensingService.delete,
  });

  /* =========================
     SELECT ONE (useQuery)
  ========================= */
  const SelectQuery = useQuery<ApiResponse>({
    queryKey: ["dispensing-select", dispensingid],
    queryFn: () =>
      dispensingService.select({
        dispensingid,
      }),
    enabled: !!dispensingid,
  });

  /* =========================
     SELECT ALL (useQuery)
  ========================= */
  const SelectAllQuery = useQuery<ApiResponse>({
    queryKey: [
      "dispensing-select-all",
      { searchText, pageNumber, pageSize, studyid },
    ],
    queryFn: () =>
      dispensingService.selectAll({
        searchtext: searchText ?? null,
        currentpage: pageNumber,
        pagesize: pageSize,
        studyid: studyid ?? null,
        status: "A",
      }),
    enabled: enableSelectall,
  });

  const DropdownQuery = useQuery<ApiResponse>({
    queryKey: ["study-ip-select-all", hasAllStudiesRight],
    queryFn: async () => {
      const result = await dispensingService.dropdown();
      if (hasAllStudiesRight) {
        return result;
      }
      const user = GetUserData();
      const hasUserStudies = Array.isArray(user?.studies) && user.studies.length > 0;
      if (hasUserStudies && result && Array.isArray(result.data)) {
        const allowedIds = new Set(
          (user.studies as any[]).map((s: any) => String(s.studyid))
        );
        return {
          ...result,
          data: result.data.filter((study: any) => allowedIds.has(String(study.studyid))),
        };
      }
      return result;
    },
    enabled: !!getStudyDropDown,
  });

   const IpDropdownQuery = useQuery<ApiResponse>({
    queryKey: ["dispensing-ip-dropdown", studyid, periodid],
    queryFn: () =>
      dispensingService.IPDropdown({
        studyid: studyid,
        periodid: periodid
      }),
    enabled: !!getIpDropdown && !!studyid && !!periodid,
  });

  const verifyPinMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: dispensingService.verifyPin,
  });

  return {
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    SelectQuery,
    SelectAllQuery,
    DropdownQuery,
    verifyPinMutation,
    IpDropdownQuery
  };
};